import Head from "next/head";
import { useRouter } from "next/router";
import React from "react";
import {
  BreadcrumbContainer,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbNav,
  BreadcrumbSeparator,
  BreadcrumbVariant,
} from "./BreadcrumbComponent.style";

export interface BreadcrumbItem {
  label: string;
  url: string;
}

export interface BreadcrumbConfig {
  [path: string]: BreadcrumbItem[];
}

interface BreadcrumbComponentProps {
  className?: string;
  hostname?: string;
  customConfig?: BreadcrumbConfig;
  label?: string;
}

// Default configuration for insurance pages
const defaultBreadcrumbConfig: BreadcrumbConfig = {
  // For www.makemytrip.com
  "/insurance": [{ label: "Home", url: "/insurance" }],
  "/insurance/listing": [
    { label: "Home", url: "/insurance" },
    { label: "Travel Insurance", url: "/insurance/listing" },
  ],
  "/insurance/traveller-details": [
    { label: "Home", url: "/insurance" },
    { label: "Traveler Details", url: "/insurance/traveller-details" },
  ],
  // For insurance.makemytrip.com (root level paths)
  "/": [{ label: "Home", url: "/insurance" }],
  "/listing": [
    { label: "Home", url: "/insurance" },
    { label: "Travel Insurance", url: "/insurance/listing" },
  ],
  "/traveller-details": [
    { label: "Home", url: "/insurance" },
    { label: "Traveler Details", url: "/insurance/traveller-details" },
  ],
};

const BreadcrumbComponent: React.FC<BreadcrumbComponentProps> = ({
  className,
  hostname: propHostname,
  customConfig,
  label: overrideLabel,
}) => {
  const router = useRouter();

  const currentPath = router.asPath;
  const pathname = currentPath.split("?")[0];
  const hostname =
    propHostname ||
    (typeof window !== "undefined"
      ? window.location.hostname
      : "www.makemytrip.com");
  const customLabel = overrideLabel?.trim();

  const generatePathBasedBreadcrumbs = () => {
    const breadcrumbs: {
      label: string;
      href: string | null;
      isActive: boolean;
    }[] = [];

    breadcrumbs.push({
      label: "Home",
      href: `https://${hostname}/`,
      isActive: false,
    });

    const countryMatch = pathname.match(/\/travel-insurance\/([^/?]+)/);
    const isOnCountryPage = countryMatch && countryMatch[1];
    const isOnTravelInsurancePage =
      pathname === "/travel-insurance" || pathname === "/travel-insurance/";

    breadcrumbs.push({
      label: "Travel Insurance",
      href: isOnTravelInsurancePage
        ? null
        : `https://${hostname}/travel-insurance/`,
      isActive: isOnTravelInsurancePage,
    });

    if (isOnCountryPage) {
      const countrySlug = countryMatch[1];
      const defaultCountryName =
        countrySlug.charAt(0).toUpperCase() + countrySlug.slice(1);

      breadcrumbs.push({
        label: customLabel || defaultCountryName,
        href: null,
        isActive: true,
      });
    }

    return breadcrumbs;
  };

  const generateConfigBasedBreadcrumbs = () => {
    const config = customConfig || defaultBreadcrumbConfig;

    let breadcrumbItems: BreadcrumbItem[] = [];

    if (config[pathname]) {
      breadcrumbItems = config[pathname];
    } else {
      for (const configPath in config) {
        if (pathname.startsWith(configPath) && configPath !== "/") {
          breadcrumbItems = config[configPath];
          break;
        }
      }
    }

    if (breadcrumbItems.length === 0) {
      const isInsuranceSubdomain = hostname.includes(
        "insurance.makemytrip.com"
      );
      breadcrumbItems = [
        {
          label: "Home",
          url: isInsuranceSubdomain ? "/" : "/insurance",
        },
      ];
    }

    return breadcrumbItems.map((item, index) => {
      const isCurrentPage = index === breadcrumbItems.length - 1;
      return {
        label: isCurrentPage && customLabel ? customLabel : item.label,
        href: isCurrentPage ? null : `https://${hostname}${item.url}`,
        isActive: isCurrentPage,
      };
    });
  };

  const isTravelInsurancePath = pathname.includes("/travel-insurance");
  const breadcrumbs = isTravelInsurancePath
    ? generatePathBasedBreadcrumbs()
    : generateConfigBasedBreadcrumbs();

  const variant: BreadcrumbVariant = isTravelInsurancePath ? "default" : "dark";

  const currentPageUrl = hostname
    ? `https://${hostname}${pathname.endsWith("/") ? pathname : `${pathname}/`}`
    : "";

  const breadcrumbSchema =
    breadcrumbs.length > 1 && hostname
      ? {
          "@context": "https://schema.org/",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((breadcrumb, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: breadcrumb.label,
            item: breadcrumb.href || currentPageUrl,
          })),
        }
      : null;

  if (breadcrumbs.length <= 1) {
    const isInsuranceSubdomain = hostname.includes("insurance.makemytrip.com");
    const isHomePage = isInsuranceSubdomain
      ? pathname === "/"
      : pathname === "/insurance";
    if (isHomePage) {
      return null;
    }
  }

  return (
    <>
      {breadcrumbSchema && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(breadcrumbSchema),
            }}
          />
        </Head>
      )}
      <BreadcrumbNav
        aria-label="Breadcrumb"
        className={className}
        variant={variant}
      >
        <BreadcrumbContainer variant={variant}>
          {breadcrumbs.map((breadcrumb, index) => (
            <React.Fragment key={index}>
              <BreadcrumbItem
                isActive={breadcrumb.isActive}
                variant={variant}
                aria-current={breadcrumb.isActive ? "page" : undefined}
              >
                {breadcrumb.href ? (
                  <BreadcrumbLink
                    href={breadcrumb.href}
                    variant={variant}
                    onClick={(e) => {
                      e.preventDefault();
                      if (breadcrumb.href) {
                        window.location.href = breadcrumb.href;
                      }
                    }}
                  >
                    {breadcrumb.label}
                  </BreadcrumbLink>
                ) : (
                  breadcrumb.label
                )}
              </BreadcrumbItem>
              {index < breadcrumbs.length - 1 && (
                <BreadcrumbSeparator aria-hidden="true" variant={variant}>
                  &gt;
                </BreadcrumbSeparator>
              )}
            </React.Fragment>
          ))}
        </BreadcrumbContainer>
      </BreadcrumbNav>
    </>
  );
};

export default BreadcrumbComponent;
